import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Tabs, Tab, Paper, Chip, Card, CardContent,
  CardActionArea, Grid, Stack, Alert,
} from '@mui/material';
import {
  ViewSidebar as SidebarIcon, FiberManualRecord as RecordIcon,
  AutoAwesome as AgentIcon, RocketLaunch as FirstRunIcon,
  Extension as ExtensionIcon, Engineering as StaffIcon,
} from '@mui/icons-material';
import ExtAdminSidebarMockup from './ExtAdminSidebarMockup';
import ExtAdminRecordingMockup from './ExtAdminRecordingMockup';
import ExtAdminAgentMockup from './ExtAdminAgentMockup';
import ExtAdminFirstRunMockup from './ExtAdminFirstRunMockup';

const PURPLE = '#7b1fa2';

const MOCKS = [
  {
    key: 'sidebar',
    label: 'Sidebar',
    icon: <SidebarIcon />,
    title: 'Authoring sidebar (normal)',
    summary: 'Script picker, step list, transport bar, runtime-fetched indicator, filter rules count.',
    component: ExtAdminSidebarMockup,
  },
  {
    key: 'recording',
    label: 'Recording',
    icon: <RecordIcon />,
    title: 'Recording state',
    summary: 'Red dot timer, live step capture, "Analyzing…" overlay after stop.',
    component: ExtAdminRecordingMockup,
  },
  {
    key: 'agent',
    label: 'Agent Mode',
    icon: <AgentIcon />,
    title: 'Agent Mode',
    summary: 'Reasoning stream, approval queue, chat input, Pause / Takeover / Stop.',
    component: ExtAdminAgentMockup,
  },
  {
    key: 'first-run',
    label: 'First Run',
    icon: <FirstRunIcon />,
    title: 'First run / bootstrap (rich)',
    summary: 'Connecting spinner → fetched confirmation → Ready state. End-user popup variant is simpler.',
    component: ExtAdminFirstRunMockup,
  },
];

const ExtAdminIndex = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const initialTab = MOCKS.findIndex((m) => m.key === slug);
  const [activeTab, setActiveTab] = useState(initialTab >= 0 ? initialTab : -1);
  useEffect(() => {
    const i = MOCKS.findIndex((m) => m.key === slug);
    setActiveTab(i >= 0 ? i : -1);
  }, [slug]);
  const handleTab = (_, v) => {
    setActiveTab(v);
    if (v === -1) navigate('/mockups/ext-admin');
    else navigate(`/mockups/ext-admin/${MOCKS[v].key}`);
  };

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <StaffIcon sx={{ fontSize: 28, color: PURPLE }} />
          <Typography variant="h4" fontWeight={700}>
            Staff / Dev Extension — Authoring in-browser
          </Typography>
          <Chip label="MOCKUP" size="small" color="warning" sx={{ fontWeight: 700 }} />
          <Chip
            label="400px side panel"
            size="small"
            sx={{ fontWeight: 600, bgcolor: '#ede7f6', color: '#4a148c' }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Hypothetical staff/developer-facing extension for power users who want to
          author and test scripts in live browser sessions. <b>NOT what end-users see</b> —
          the end-user runtime surface lives at <code>/mockups/ext-v5</code>.
          Most of this functionality is already in the Automatrix web dashboard
          (Watch Mode, Agent Mode, script editing); this surface exists for engineers
          debugging in real Chrome tabs without needing DevTools + the dashboard
          side-by-side.
        </Typography>
      </Box>

      <Alert severity="warning" sx={{ mb: 2 }}>
        <b>Surface scope reminder.</b> These rich authoring panels are NOT the
        extension inmates have on facility Chromebooks. That surface is a silent
        script runner: a toolbar icon + a tiny status popup. See <code>/mockups/ext-v5</code>.
      </Alert>

      {/* Tab navigation */}
      <Paper sx={{ mb: 2 }} elevation={1}>
        <Tabs
          value={activeTab}
          onChange={handleTab}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              minHeight: 52,
            },
          }}
        >
          <Tab
            value={-1}
            icon={<ExtensionIcon />}
            iconPosition="start"
            label="Gallery"
          />
          {MOCKS.map((m, idx) => (
            <Tab
              key={m.key}
              value={idx}
              icon={m.icon}
              iconPosition="start"
              label={m.label}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Gallery (cards) */}
      {activeTab === -1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Each mock renders a faux side panel at the true 400px width.
            Click any card to open it full-width.
          </Alert>
          <Grid container spacing={2}>
            {MOCKS.map((m, idx) => (
              <Grid item xs={12} sm={6} md={3} key={m.key}>
                <Card elevation={2}>
                  <CardActionArea onClick={() => handleTab(null, idx)} sx={{ minHeight: 160 }}>
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Box sx={{ color: PURPLE }}>{m.icon}</Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {m.title}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                        {m.summary}
                      </Typography>
                      <Typography variant="caption" sx={{
                        display: 'block', mt: 1.5, color: PURPLE, fontFamily: 'monospace', fontSize: '0.7rem',
                      }}>
                        /mockups/ext-admin · {m.key}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Individual mock */}
      {activeTab >= 0 && MOCKS[activeTab] && (
        <Box>
          {React.createElement(MOCKS[activeTab].component)}
        </Box>
      )}
    </Box>
  );
};

export default ExtAdminIndex;
